import { useCallback, useEffect, useRef, useState } from "react";

import { useChatStream } from "./useChatStream";
import { useCreateConversation } from "./useCreateConversation";
import { useGuestToken } from "./useGuestToken";
import { useEnhancedChatSendMessage } from "./useSendMessage";
import { useSendFile } from './useSendFile';
import type { ChatSession, SalesforceChatConfig } from "../types/chat";

export function useSalesforceChat(config: SalesforceChatConfig) {
  const [session, setSession] = useState<ChatSession | null>(null);
  const [initializationRequested, setInitializationRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMounted = useRef(true);

  const { isPending: tokenIsPending, mutate: getToken } = useGuestToken(config);
  const { isPending: conversationalIsPending, mutate: createConversation } =
    useCreateConversation(config);
  const sendMessageMutation = useEnhancedChatSendMessage(config);
  const sendFileSalesforce = useSendFile(config);

  const stream = useChatStream(config, session ?? undefined);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const startSession = useCallback(async () => {
    if (initializationRequested) return;

    setInitializationRequested(true);
    setError(null);
    await getToken(undefined, {
      onSuccess: (guestToken) => {
        const accessToken = guestToken.accessToken!;
        if (!accessToken) throw new Error("Falha ao gerar accessToken.");
        createConversation(
          { accessToken },
          {
            onSuccess: (data) => {
              const conversationId = data?.conversationId;
              if (!conversationId)
                throw new Error("Falha ao criar a conversa.");
              if (isMounted.current) {
                setSession({ accessToken, conversationId });
              }
            },
            onError: (err) => {
              const msg = err?.message || "Erro desconhecido ao criar o chat.";
              setError(msg);
            },
          }
        );
      },
      onError: (err) => {
        const msg = err?.message || "Erro desconhecido ao iniciar o chat.";
        setError(msg);
      },
    });
  }, [initializationRequested]);

  const loading =
    initializationRequested &&
    (!session || tokenIsPending || conversationalIsPending);

  return {
    startSession,
    sendMessage: sendMessageMutation.mutate,
    sendFile: sendFileSalesforce.mutate,
    messages: stream.messages,
    typing: stream.typing,
    connected: stream.connected,
    loading,
    session,
    error,
  };
}
